import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {RecipientsComponent} from 'src/app/components';
import {AlertManagerState, Application, Client, InputTypeEnum, Message, Pageable, Recipient, Tag, Template} from 'src/app/models';
import {DataService, IndividualsService, MessagesService, SnackbarService, TemplateService} from 'src/app/services';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {COMMA, ENTER, TAB} from '@angular/cdk/keycodes';
import {ClipboardService} from 'ngx-clipboard';
import {FormControl, NgForm} from '@angular/forms';
import {safeString, stringCompare} from '../../utilities/string';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatDialog} from '@angular/material/dialog';
import {WarningPopupComponent} from './warning-popup/warning-popup.component';

@Component({
    selector: 'app-single-alert', templateUrl: './send-message.component.html', styleUrls: ['./send-message.component.scss']
})
export class SendMessageComponent implements OnInit {
    @Input() recipientsChange: Recipient[];
    chipArray: Recipient[] = [];
    selectedTemplate: Template;
    messageRequest: Message;
    messagePreview: String = '';
    isEditingTemplate = true;
    loadingSomething = false;
    userInfoReady = false;
    messageReady = false;
    saveDisabled = false;
    appSelected = false;
    confirmed = false;
    selectable = true;
    removable = true;
    showTruncateError = false;

    checkmark = 'uncheck';

    applicationChange = new Subject<string>();
    templateChange = new Subject<string>();
    clientChange = new Subject<string>();
    senderChange = new Subject<string>();
    applications: Application[] = null;
    clients: Client[] = [];
    templates: Template[];

    defaultTags: Tag[] = [];
    prevTags: Tag[] = [];
    tags: Tag[] = [];

    selectedAppName;
    templateMessage;
    currentClient;
    buttonValue;
    sentBy;
    mainId;
    recipientCount: number=0;
    // default to 20
    recipientThreshold: number=20;
    recipientWarningMessage: string;

    // Autocomplete
    searchVar = '';
    filter = '';
    pageNum = 1;
    templateSearch;
    autocompleteTemplates: Template[] = [];
    autocompleteTemplatesFormatted: string[] = [];

    DROPDOWN: InputTypeEnum = InputTypeEnum.DROPDOWN;
    RADIO: InputTypeEnum = InputTypeEnum.RADIO;
    TEXTBOX: InputTypeEnum = InputTypeEnum.TEXTBOX;
    TEXTAREA: InputTypeEnum = InputTypeEnum.TEXTAREA;
    SENTBY: InputTypeEnum = InputTypeEnum.SENTBY;
    CLIENTS: InputTypeEnum = InputTypeEnum.CLIENTS;
    APPLICATIONS: InputTypeEnum = InputTypeEnum.APPLICATIONS;
    DATE: InputTypeEnum = InputTypeEnum.DATE;
    CONFIRM: InputTypeEnum = InputTypeEnum.CONFIRM;

    senders = null;
    separatorKeysCodes: number[] = [ENTER, COMMA, TAB];

    @ViewChild('recipientsComponent', {static: false}) recipientsComponent: RecipientsComponent;
    @ViewChild('messageForm', {static: false}) messageForm: NgForm;

    clientsImpacted = [];
    clientControl = new FormControl([]);
    appList = [];
    appControl = new FormControl([]);

    tagPattern = new RegExp(/{{[^{}]*}}/g);
    bracketsPattern = new RegExp(/([{}])/g);
    confirmNewlineRegex = new RegExp(/(\n|\r)\s*{{confirm}}\s*(\n|\r)/g);

    constructor(private messagesService: MessagesService, private router: Router, private route: ActivatedRoute,
                private snackbarService: SnackbarService, private templateService: TemplateService, private location: Location,
                private dataService: DataService, private _clipboardService: ClipboardService, private individualService: IndividualsService,
                private dialog: MatDialog) {
    }

    ngOnInit() {
        this.dataService.getUserInfo().subscribe(resi => {
            this.sentBy = resi['name'];
            this.userInfoReady = true;
        });

        this.dataService.getSetting('RECIPIENT_WARNING').subscribe( {
            next: (threshold:string) => {
                this.recipientThreshold = Number(threshold);
            },
            error: () => {
            }
        })

        this.setupOnChangeSearches();

        // redirect to sendMessage from alertStatus
        this.location.replaceState('\sendMessage');

        // populate all the inputs from the original/parent message
        this.route.queryParams.subscribe((params: any) => {
            this.messageRequest = {recipients: this.chipArray, message: ''} as Message;
            this.templateMessage = this.messageRequest.message;

            if (params['id'] !== undefined) {
                this.mainId = params['id']; // not sure what your id is called here, it's defined in your route
                this.buttonValue = params['value'];
                this.messagesService.getAlertDetailsDataReply(this.mainId).subscribe((res: any) => {
                    this.messagesService.getTagsByMessage(this.mainId).subscribe({
                        next: (tags: any) => {
                            this.prevTags = tags.content || tags;
                            if (this.prevTags.some(tag => tag.name === 'clients' && tag.tag_text.trim().length > 0)) {
                                this.clientsImpacted = this.prevTags.find(tag => tag.name === 'clients').tag_text.split(/,/);
                            }
                            if (this.prevTags.some(tag => tag.name === 'applications' && tag.tag_text.trim().length > 0)) {
                                this.appList = this.prevTags.find(tag => tag.name === 'applications').tag_text.split(/,/);
                            }

                            this.templateService.getTemplateById(res.template_id)
                                .subscribe(template => this.selectedTemplate = template)
                                .add(() => this.initializeTemplate());

                        }, error: error => this.handleHttpError(error)
                    });
                    if (this.buttonValue === 'reply') {
                        this.recipientsComponent.parseReply(res.recipients);
                    }
                });
            } else {
                this.templateService.getTemplateNamesPageSize('Send Message Default', 1)
                    .subscribe(template => this.selectedTemplate = template.content[0])
                    .add(() => this.initializeTemplate());
            }
            this.loadingSomething = false;
            this.messageReady = true;
        });

        this.templateChange.pipe(distinctUntilChanged()).subscribe((term: string) => {
            this.searchVar = term;
            if (term != null && term.trim().length > 1) {
                this.templateService.getTemplate(term, AlertManagerState.NO).subscribe(templates => {
                    this.autocompleteTemplates = templates.content;
                    this.autocompleteTemplatesFormatted = templates.content.map((template) => template.name);
                });
            } else {
                this.autocompleteTemplates = [];
                this.autocompleteTemplatesFormatted = [];
            }
        });
    }

    setupOnChangeSearches(): void {
        const delay = 750;

        this.applicationChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.getApplications(term));

        this.clientChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.getClientNames(term));

        this.senderChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.getSenders(term));
    }

    getClientNames(filterValueClient: string): void {
        if (filterValueClient != null && filterValueClient.length > 1) {
            this.dataService.getClientNames(filterValueClient).subscribe({
                next: clients => this.clients = clients, error: error => this.handleHttpError(error)
            });
        } else {
            this.clients = [];
        }
    }

    getApplications(filterValue: string): void {
        filterValue = filterValue && filterValue.toLowerCase ? filterValue.toLowerCase() : '';
        if (filterValue != null && filterValue.length > 1) {
            this.dataService
                .getApplicationNames(filterValue)
                .subscribe({
                    next: applications => this.applications = applications, error: error => this.handleHttpError(error)
                });
        } else {
            this.applications = [];
        }
    }

    getSenders(filterValue: string): void {
        filterValue = filterValue && filterValue.toLowerCase ? filterValue.toLowerCase() : '';
        if (filterValue != null && filterValue.length > 1) {
            this.individualService.getIndividuals(filterValue).subscribe({
                next: individualsAndDevices => this.senders = individualsAndDevices, error: error => this.handleHttpError(error)
            });
        } else {
            this.senders = [];
        }
    }

    getTemplates(filterValue: string): void {
        filterValue = (filterValue && filterValue.toLowerCase ? filterValue.toLowerCase() : '');
        if (filterValue != null && filterValue.length > 1) {
            this.templateService.getTemplate(filterValue, AlertManagerState.NO).subscribe({
                next: templates => this.templates = templates.content, error: error => this.handleHttpError(error)
            });
        }
    }

    removeClient(clientName): void {
        this.clientsImpacted = this.clientsImpacted.filter((p: any) => (clientName !== p));
        this.tags.find(tag => tag.name === 'clients').tag_text = this.clientsImpacted.toString().replace(/,/g, ', ');
        this.parseMessage();
    }

    appFieldBlur(event?: any, input?: any): void {
        setTimeout(() => {
            if (this.selectedAppName != null && this.selectedAppName.trim() !== '') {
                this.addApp(event, input);
            }
        }, 150);
    }

    addApp(event?: any, appInput?): void {
        this.applications = [];
        this.applicationChange.next('');
        const value = event.value ? event.value : event.application_name;

        if (value === undefined) {
            if (this.selectedAppName != null && !this.appList.includes(this.selectedAppName.trim())) {
                if (this.selectedAppName !== '') {
                    this.appList.push(this.selectedAppName.trim());
                }
                this.selectedAppName = '';
                this.appSelected = true;
                this.parseMessage();
            } else {
                this.snackbarService.openSnackBar('Duplicate Application provided - try again ', true);
            }
        } else {
            if (this.isDuplicateApp(value) === true) {
                this.snackbarService.openSnackBar('Duplicate Application provided - try again ', true);
            } else {
                this.appList.push(value);
                this.selectedAppName = '';
                this.appSelected = false;
                this.parseMessage();
            }
        }
        this.appControl.updateValueAndValidity();

        this.selectedAppName = '';

        if (appInput) {
            appInput.value = '';
        }
        if (event.value) {
            event.value = '';
        }

        if (this.tags.some(tag => tag.name === 'applications')) {
            this.tags.find(tag => tag.name === 'applications').tag_text = this.appList.toString().replace(/,/g, ', ');
        }
    }

    isDuplicateApp(appName): boolean {
        const found = this.appList.find(p => p === appName);
        return Boolean(found);
    }

    getAppName(app): string {
        return app ? app.application_name : '';
    }

    getClientName(client): string {
        return client ? client.client_name : '';
    }

    removeApp(appName): void {
        this.appList = this.appList.filter((p: any) => (appName !== p));
        this.tags.find(tag => tag.name === 'applications').tag_text = this.appList.toString().replace(/,/g, ', ');
        this.parseMessage();
    }

    clientFieldBlur(event?: any, input?: any): void {
        setTimeout(() => {
            if (this.currentClient != null && this.currentClient.trim() !== '') {
                this.addClient(event, input);
            }
        }, 150);
    }

    addClient(event?: any, clientInput?): void {
        this.clientChange.next('');
        this.clients = [];
        const value = event.value ? event.value : event.client_name;

        if (value != undefined && this.currentClient != null) {
            if (this.isDuplicateClient(value)) {
                this.snackbarService.openSnackBar('Duplicate Client provided - try again ', true);
            } else {
                this.clientsImpacted.push(value);
                this.currentClient = '';
                this.parseMessage();
            }
        }
        this.clientControl.updateValueAndValidity();

        this.currentClient = '';
        if (clientInput) {
            clientInput.value = '';
        }
        if (event.value) {
            event.value = '';
        }
        if (this.tags.some(tag => tag.name === 'clients')) {
            this.tags.find(tag => tag.name === 'clients').tag_text = this.clientsImpacted.toString().replace(/,/g, ', ');
        }
    }

    isDuplicateClient(client: void): boolean {
        const found = this.clientsImpacted.find(p => p === client);
        return Boolean(found);
    }

    handleHttpError(response): void {
        this.snackbarService.openSnackBar(response.error.message.length !== 0 ? `${response.error.message} : System error` : 'session timeout', true);
    }


    sendAsync(){
        this.messagesService.sendMessage(this.messageRequest).subscribe({
            next: (response: any) => {
                // API returns HttpResponse with:
                // - V2 (FF_MessageType=true): 200 with messageRequestId as plain text in body
                // - V1 (FF_MessageType=false): 204 with empty body
                let messageRequestId = null;
                if (response) {
                    if (response.status === 200 && response.body) {
                        // V2: Extract and validate messageRequestId from response body
                        const trimmed = response.body.trim();
                        // Validate format: messageRequest-{uuid}
                        if (trimmed && trimmed.startsWith('messageRequest-') && trimmed.length > 15) {
                            messageRequestId = trimmed;
                        }
                    } else if (response.status === 204) {
                        // V1: No content, messageRequestId remains null
                        messageRequestId = null;
                    }
                }
                // Navigate to success page (will decide routing based on FF and messageRequestId)
                this.router.navigate(['success'], {
                    state: { messageRequestId }
                }).then(() => this.reset())
            },
            error: error => {
                this.snackbarService.openSnackBar('There was an error sending the message: ' + error.error.message, true);
                this.parseMessage();
            }
        }).add(() => {
            this.saveDisabled = false;
            this.messageReady = true;
            this.loadingSomething = false;
        });
    }

    sendMessage() {
        this.loadingSomething = true;
        this.messageRequest.tags = this.tags;
        if (this.recipientsComponent.recipients.length === 0) {
            this.snackbarService.openSnackBar('Please fill out at least one value for Individual or Group', true);
            this.loadingSomething = false;
            return;
        }

        this.messageRequest.recipients = this.recipientsComponent.recipients.map((recipient) => new Recipient(recipient.recipient_type, recipient.recipient_id, recipient.response_required));

        if (!this.messageForm.form.valid) {
            this.snackbarService.openSnackBar('A required tag is empty.', true);
            this.loadingSomething = false;
            return;
        }

        // If The Template Has Not Changed (No Adhoc Changes), Do Not Send A Message In messageRequest Object
        if (this.selectedTemplate.template_text === this.templateMessage) {
            this.messageRequest.message = undefined;
        }

        this.messageReady = false;
        this.saveDisabled = true;
        this.messageRequest.alert = false;
        this.messageRequest.parent_id = null;
        if (this.mainId !== undefined) {
            this.messageRequest.parent_id = this.mainId;
        }

        //Message IGO -> determine if the recipient threshold has been met
        this.messagesService.getRecipientCount(this.messageRequest.recipients).subscribe( {
            next: (count:number) => {
                this.recipientCount = count;

                if(this.recipientCount>=this.recipientThreshold) {
                    this.recipientWarningMessage = 'This message will be sent to a large number of recipients, ' + this.recipientCount + '.';
                    const dialogRef =this.dialog.open(WarningPopupComponent, {
                        width: '325px', height: '205px', data: {
                            dataKey: this.recipientWarningMessage
                        }
                    });
                    dialogRef.afterClosed().subscribe( result => {
                        if (result) {
                            this.sendAsync()
                        }
                        else {
                            this.messageReady = true;
                            this.saveDisabled = false;
                            this.loadingSomething = false;
                        }
                    })
                } else {
                    this.sendAsync()
                }
            },
            error: error => {
                this.snackbarService.openSnackBar('Please review your recipients and retry. The following error was received: ' + error.error.message, true);
            }
        });
    }

    parseMessage(): void {
        setTimeout(() => {
            // this prevents the confirmation tag from being on a separate line
            this.templateMessage = this.templateMessage.replace(this.confirmNewlineRegex, "{{confirm}}\n");

            let tagsList = [];
            let maxLength = this.getMaxMsgLength();
            let tagNames = this.templateMessage.match(this.tagPattern) || [];
            tagNames = tagNames.filter((tagName, i) => tagNames.indexOf(tagName) === i); //removes duplicates for faster processing of tags
            tagNames.forEach((tagName, i) => {
                if (tagName.length > 54) {
                    this.templateMessage = this.templateMessage.replaceAll(tagName, tagName.slice(0, 52) + '}}');
                    tagNames[i] = tagName.slice(0, 52).replace(this.bracketsPattern, '');
                    this.snackbarService.openSnackBar('Tag names can only be up to 50 characters long', true);
                } else if (tagName.length < 6) { // two characters plus {{}}
                    this.templateMessage = this.templateMessage.replaceAll(tagName, '');
                    tagNames[i] = null; // mark tags for delete
                    this.snackbarService.openSnackBar('Tag names must be 2 characters or longer', true);
                } else {
                    tagNames[i] = tagName.replace(this.bracketsPattern, '');
                }
            });
            tagNames = tagNames.filter(tagName => tagName != null); // remove null tags

            let message = this.templateMessage; //apply the template

            //process tags
            if (tagNames != []) {
                tagNames.forEach(tagName => {
                    const defaultTag = this.isEmpty(this.defaultTags) ? null : this.defaultTags.find(tag => tag != undefined && tag.name === tagName);
                    const currTag = this.isEmpty(this.tags) ? null : this.tags.find(tag => tag != undefined && stringCompare(tag.name, tagName.replace(/{{|}}/g, '')));
                    if (tagName === 'confirm') {
                        message = message.replace(new RegExp(`{{${tagName}}}`, 'g'), '');
                    }
                    if (currTag && currTag.tag_text) {
                        message = message.replace(new RegExp(`{{${tagName}}}`, 'g'), currTag.tag_text);
                    } else {
                        const prevTagFound = this.prevTags?.find(tag => tag.name === tagName) || defaultTag;
                        if (prevTagFound && prevTagFound.tag_text !== '') {
                            message = message.replace(new RegExp(`{{${tagName}}}`, 'g'), prevTagFound.tag_text);
                        }
                        if (currTag == null) {
                            const newTagText = prevTagFound?.tag_text || '',
                                newTagOptions = defaultTag?.tag_options || prevTagFound?.tag_options || null,
                                newTagPrompt = defaultTag?.prompt || prevTagFound?.prompt || null,
                                newTagInputType = defaultTag?.input_type || prevTagFound?.input_type || this.templateService.getInputType(tagName),
                                newTagRequired = defaultTag ? defaultTag.required : prevTagFound ? prevTagFound.required : true;
                            this.tags.push(new Tag(null, null, tagName, newTagText, newTagRequired, false, newTagOptions, newTagPrompt, newTagInputType));
                        }
                    }
                    tagsList.push(tagName);
                });
                this.tags = this.isEmpty(this.tags) ? [] : this.tags.filter(tag => tagsList.includes(tag.name)).sort((tagA, tagB) => tagsList.findIndex(indexTag => indexTag === tagA.name) - tagsList.findIndex(indexTag => indexTag === tagB.name));
                if (message.trim().length > maxLength || stringCompare(message.slice(maxLength - 8, maxLength), 'TRUNCATED')) {
                    this.messageRequest.message = message.trim().slice(0, maxLength - 10) + ' TRUNCATED';
                    this.showTruncateError = true;
                } else {
                    this.messageRequest.message = message != null ? message.trim() : '';
                    this.showTruncateError = false;
                }
                this.messagePreview = this.messageRequest.message;

                return;
            }

            this.tags = undefined;
            if (this.templateMessage.length > maxLength || stringCompare(this.templateMessage.slice(maxLength - 10, maxLength), ' TRUNCATED')) {
                this.messageRequest.message = this.templateMessage.slice(0, maxLength - 10) + ' TRUNCATED';
                this.showTruncateError = true;
            } else {
                this.messageRequest.message = this.templateMessage;
                this.showTruncateError = false;
            }
            this.messagePreview = this.messageRequest.message;
        }, 200);
    }

    selectTemplate(event: MatAutocompleteSelectedEvent) {
        this.loadingSomething = true;
        this.selectedTemplate = this.autocompleteTemplates.find(template => template.name === event.option.value);
        this.autocompleteTemplatesFormatted = [];
        this.onTemplateChanged('');

        this.initializeTemplate();

        this.loadingSomething = false;
    }

    initializeTemplate(): void {
        this.messageRequest.template_id = this.selectedTemplate.id;
        this.messageRequest.template_name = this.selectedTemplate.name;
        this.templateMessage = this.selectedTemplate.template_text;
        if (this.prevTags == undefined) {
            this.prevTags = this.tags;
        } else {
            this.tags?.forEach(tag => {
                if (tag.tag_text != null) {
                    const prevTagFound = this.prevTags.find(prevTag => tag.name === prevTag.name);
                    if (prevTagFound != null) {
                        this.prevTags.find(prevTag => stringCompare(tag.name, prevTag.name)).tag_text = tag.tag_text;
                    } else {
                        this.prevTags.push(tag);
                    }
                }
            });
        }
        this.tags = [];
        this.defaultTags = this.selectedTemplate.tags;
        this.defaultTags.forEach(tag => tag.tag_text = stringCompare(tag.name, 'sentBy') ? this.sentBy : safeString(tag.tag_text));
        this.parseMessage();

        this.loadingSomething = false;
    }

    reset(template?: string) {
        this.checkmark = 'uncheck';
        this.loadingSomething = true;
        this.prevTags = [];
        this.tags = [];
        this.appList = [];
        this.clientsImpacted = [];
        this.recipientsComponent?.resetInputField();
        if (template) {
            this.templateService.getTemplateNamesPageSize(template, 1)
                .subscribe(template => this.selectedTemplate = template.content[0])
                .add(() => this.initializeTemplate());
        } else {
            this.initializeTemplate();
        }
    }

    ready(): boolean {
        return this.messageReady && this.userInfoReady;
    }

    toggleEditing() {
        this.isEditingTemplate = !this.isEditingTemplate;
    }

    isEmpty(array) {
        return array == undefined || array.length == undefined || array.length == 0;
    }

    copy(message): void {
        this.checkmark = 'check';
        this._clipboardService.copyFromContent(message);
        setTimeout(() => {
            if (this.checkmark === 'check') {
                this.checkmark = 'out';
            }
        }, 5000);
    }

    load(page: any) {
        if (page.data === undefined) {
            this.pageNum = page;
            this.templateService.getTemplateNamesPageSize(this.filter, page * 15).subscribe((templates: Pageable<Template>) => {
                this.autocompleteTemplates = templates.content;
                this.autocompleteTemplatesFormatted = [];
                this.autocompleteTemplates.forEach(template => this.autocompleteTemplatesFormatted.push(template.name));
            });
        }
    }

    /**
     * Add Individual / Device search onChange event handler to trigger Subject
     */
    onTemplateChanged(event: any) {
        if (event.data === undefined) {
            this.templateSearch = event;
            this.filter = event;
            this.templateChange.next(event);
        }
    }

    isInvalid(): boolean {
        if (this.messageForm) {
            let app = this.tags.find(tag => tag.name === 'applications');
            let client = this.tags.find(tag => tag.name === 'clients');
            let date = this.tags.find(tag => tag.name === 'date');
            return !this.messageForm.form.valid || (!!app && (app.required && app.tag_text.trim().length == 0)) || (!!client && (client.required && client.tag_text.trim().length == 0) || (!!date && (date.required && date.tag_text.trim().length == 0)));
        }
        return true;
    }

    dateChanged(tag: Tag, date: Date) {
        tag.tag_text = date.toString();
        this.parseMessage();
    }

    checkTemplateLength() {
        if (this.templateMessage) {
            if (this.templateMessage.length > 2048) {
                this.snackbarService.openSnackBar('Max length reached', true);
                this.templateMessage = this.templateMessage.slice(0, 2048);
            }
        }
    }

    getMaxMsgLength(): number {
        let ack = false;
        if (this.recipientsComponent) {
            this.recipientsComponent.recipients.forEach(function (recipient) {
                if (recipient.response_required) {
                    ack = true;
                }
            });
        }
        if (ack) {
            if (this.messagePreview.length > 1000 && !this.showTruncateError) {
                this.parseMessage(); // If the length is above the new limit, parse message to show error
            }
            return 1000;
        } else {
            return 1600;
        }
    }
}
